<script setup lang="ts">
// Vendor
import { differenceInCalendarDays } from "date-fns";
import { createMarriottReservationLink } from "~/utils/createMarriottLink";

// Utils
import { formatDate } from "@citizenm/foundation/utils/date";
import { typeOfStay } from "@citizenm/tracking/utils/typeOfStay";

// Constants
import {
  MARRIOTT_REDIRECT_TIMEOUT,
  RATES_INPUT_DATE_FORMAT,
} from "~/shared/constants";

// types
import type { Continent } from "~/shared/types/continents";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";

import RedirectModal from "@citizenm/foundation/components/RedirectModal/RedirectModal.vue";

const breakpoints = useBreakpoints(breakpointsTailwind, { ssrWidth: 1024 });
const mdAndUp = breakpoints.greaterOrEqual("md");
const travelDataStore = useTravelDataStore();
const { openModal, closeTopModal } = useModal();

const { hotels } = useHotels();
const { t, locale } = useI18n();

/**
 *
 * The current flow relies heavily on legacy hotel data and a refactor to the new useHotels composable is not worth it at this point in time.
 * UseHotels contains the CRS flag, hence why this is added in as additional data.
 */
const additionalDataForSelectedHotel = computed(() =>
  hotels.value.find(
    (hotel) => hotel.propertyCode === travelDataStore.hotelCodes,
  ),
);

const isSelectedHotelCrsEnabled = computed(() => {
  return additionalDataForSelectedHotel.value?.crsEnabled;
});

const {
  bar = false,
  submitUrl = "",
  submitLabel = null,
  hasShadow = false,
  disabledLocation = false,
  disabledButton = false,
} = defineProps<{
  bar?: boolean;
  submitUrl?: string;
  submitLabel?: string | null;
  hasShadow?: boolean;
  disabledLocation?: boolean;
  disabledButton?: boolean;
  translations?: Record<string, string>;
}>();

const locationType = ref<string>("");

const continents = computed(() =>
  hotels.value.reduce<Continent[]>((acc, item) => {
    let continent = acc.find((c) => c.cfId === item.continent.contentId);
    if (!continent && item.continent) {
      // Continent is not found, create a new one
      continent = {
        cfId: item.continent.contentId,
        id: item.continent.contentId,
        title: item.continent.title,
        type: "continent",
        cities: [],
      };
      acc.push(continent);
    }

    let city = continent?.cities?.find((c) => c.cfId === item.city.contentId);
    if (!city && item.city) {
      // City is not found, create a new one
      city = {
        cfId: item.city.contentId,
        id: item.city.contentId,
        code: item.city.code,
        title: item.city.title,
        type: "city",
        properties: [],
      };

      if (continent?.cities) {
        continent.cities.push(city);
      }
    }

    if (city?.properties) {
      city.properties.push({
        cfId: item.hotelContentfulId,
        id: item.propertyCode,
        title: item.name,
        type: "property",
        code: item.propertyCode,
      });
    }

    return acc;
  }, []),
);

const handleSubmit = () => {
  if (window.cmtr) {
    const search = {
      event_name: "hotel_search",
      arrival: formatDate(travelDataStore.arrivalDate, RATES_INPUT_DATE_FORMAT),
      departure: formatDate(
        travelDataStore.departureDate,
        RATES_INPUT_DATE_FORMAT,
      ),
      duration: differenceInCalendarDays(
        travelDataStore.departureDate,
        travelDataStore.arrivalDate,
      ),
      stay_type: typeOfStay(
        travelDataStore.arrivalDate,
        travelDataStore.departureDate,
      ),
      guests: travelDataStore.guestsPerRoom,
      rooms: travelDataStore.numberOfRooms,
    };

    if (locationType.value === "continent") {
      search.destination = "continent";
      search.continent = travelDataStore.hotelCodes;
    } else if (locationType.value === "city") {
      search.destination = "city";
      search.city = travelDataStore.hotelCodes;
    } else {
      search.destination = "hotel";
      search.hotel = travelDataStore.hotelCodes;
    }

    // @ts-ignore
    window.cmtr.callDDM("search.hotel_search", {
      search,
    });
  }
  if (additionalDataForSelectedHotel.value && isSelectedHotelCrsEnabled.value) {
    const link = createMarriottReservationLink({
      crsCode: additionalDataForSelectedHotel.value.crsCode,
      locale: locale.value,
      fromDate: travelDataStore.arrivalDate,
      toDate: travelDataStore.departureDate,
      rooms: travelDataStore.numberOfRooms,
      guests: travelDataStore.guestsPerRoom * travelDataStore.numberOfRooms,
    });

    openModal(RedirectModal);
    setTimeout(() => {
      closeTopModal();
      navigateTo(link, { external: true });
    }, MARRIOTT_REDIRECT_TIMEOUT);
    return;
  }
  navigateTo({
    path: submitUrl,
    query: {
      location: travelDataStore.hotelCodes,
      type: locationType.value,
      startdate: formatDate(
        travelDataStore.arrivalDate,
        RATES_INPUT_DATE_FORMAT,
      ),
      enddate: formatDate(
        travelDataStore.departureDate,
        RATES_INPUT_DATE_FORMAT,
      ),
      guestsPerRoom: travelDataStore.guestsPerRoom,
      numberOfRooms: travelDataStore.numberOfRooms,
    },
  });
};
</script>

<template>
  <div
    class="hotel-search-bar"
    :class="{
      'has-shadow': hasShadow,
      'show-as-bar': bar,
      'show-as-widget': !bar,
    }"
  >
    <form
      method="get"
      :action="submitUrl"
      class="hotel-search-bar__form"
      @submit.prevent="handleSubmit"
    >
      <H level="3" size="7">{{ t("headline") }} </H>
      <div class="hsb-location-select hsb-row">
        <InputLocation
          name="location"
          v-model="travelDataStore.hotelCodes"
          v-model:location-type="locationType"
          :placeholder="t('website.bookingbar.destination')"
          :empty-value="t('website.societym.panel_clickChooseCityHotel')"
          :all-in="t('website.general.location.xHotelsInLocation')"
          :continents="continents"
          fullwidth
          :disabled="disabledLocation"
        />
        <p
          class="hotel-search-bar__footnote"
          v-if="!mdAndUp && isSelectedHotelCrsEnabled"
        >
          {{ t("website.bookingbar.redirect_alert") }}
        </p>
      </div>
      <div class="hsb-date-select hsb-row">
        <ClientOnly fallback-tag="div" fallback="loading...">
          <InputDate
            name="dates"
            v-model:start="travelDataStore.arrivalDate"
            v-model:end="travelDataStore.departureDate"
            :placeholder="t('website.bookingbar.dates')"
            fullwidth
          />
        </ClientOnly>
      </div>

      <div class="hsb-people-select hsb-row">
        <InputPersonGuestCount
          name="count"
          v-model:guests-per-room="travelDataStore.guestsPerRoom"
          v-model:rooms="travelDataStore.numberOfRooms"
          :placeholder="t('website.bookingbar.guestsAndRooms')"
          :button-text="t('website.societym.panel_select_people')"
          :more-than-ten-text="t('10+')"
        />
      </div>

      <Button
        type="submit"
        :disabled="disabledButton"
        class="hsb-submit"
        :fullwidth="!bar"
        tracking-area="search bar"
        >{{
          isSelectedHotelCrsEnabled
            ? t("website.bookingbar.search_marriott")
            : t("website.button.search")
        }}<Icon
          style="color: white"
          class="hsb-submit__icon"
          v-if="isSelectedHotelCrsEnabled"
          name="foundation:external-link"
      /></Button>
    </form>

    <p
      class="hotel-search-bar__footnote"
      v-if="mdAndUp && isSelectedHotelCrsEnabled"
    >
      {{ t("website.bookingbar.redirect_alert") }}
    </p>
  </div>
</template>

<style lang="scss">
.hotel-search-bar {
  width: 100%;
  background: var(--primary-white);
  border-radius: var(--border-radius);

  &__form {
    display: flex;
    flex-direction: column;
    gap: var(--space-16);
  }

  &.has-shadow {
    padding: var(--space-16);
    box-shadow: var(--shadow);
  }

  .inp {
    font-family: var(--font-family-bold);
  }

  &.show-as-widget {
    .citizem-person-rooms-count-wrap {
      max-width: none;
    }
  }

  &.show-as-bar {
    .headline {
      display: none;
    }
  }

  @include md {
    &__form {
      flex-direction: row;
    }

    &.show-as-bar {
      flex-flow: row nowrap;

      .hsb-row {
        width: 29%;
      }

      .hsb-submit {
        display: flex;
        gap: var(--cm-spacing-2);
        width: auto;
        overflow: unset;

        &__icon {
          color: var(--primary-white);
        }
      }
    }

    &.has-shadow {
      padding: var(--space-24);
    }
  }

  &__footnote {
    margin: 0;
    margin-block-start: var(--cm-spacing-2);
  }
}

.hsb-row {
  width: 100%;

  .citizem-person-count-wrap {
    max-width: none;
  }
}
</style>
